// Drafts and Mark as ready (DES1 DRAFTS-AND-READY.md; ARCH drafts-and-ready-authority-20261006): the bar state chip, the
// change strip, the kept-edits notice, the Versions badges and the per-kind tab words. Namespace `ready`; strings: DES2
// drafts-ready-24.json and saved-item-tabs-24.json. {kind} in versionsHelp is never inflected.
import type { NamespaceCatalog } from '@/i18n/ns';

const en = {
  draft: 'Draft',
  ready: 'Ready',
  changedSince: 'Changed since ready',
  markAsReady: 'Mark as ready',
  peopleSee: 'People see v{n} until you do',
  marked: 'Marked v{n} as ready',
  unmark: 'Unmark as ready',
  whatChanged: 'What changed',
  goBack: 'Go back',
  wentBack: 'Went back to v{n}',
  editsKept: 'Unsaved edits kept',
  created: 'Created',
  markThisReady: 'Mark this as ready',
  tabActivity: 'Activity',
  tabDocument: 'Document',
  tabSlides: 'Slides',
  versionsHelp: 'Changes to this {kind}: what it looks like and does.',
  activityHelp: 'What happened while people used it.',
  activityEmpty: 'Nothing has happened yet.',
  dataSummary: '{count} saved · latest {time}',
  dataShowMore: 'Show more',
  dataShowRaw: 'Show raw',
  dataCopy: 'Copy',
  dataMoreColumns: '+{n} more',
  dataItemsNoun: 'one:item|other:items',
  dataAutoFields: 'Fields chosen automatically',
  sharedBeforeReady: 'Shared people see the current version until you mark it ready.',
};

export type ReadyCopyKey = keyof typeof en;
export const readyCopy: NamespaceCatalog<ReadyCopyKey> = {
  en,
  sv: {
    draft: 'Utkast',
    ready: 'Klart',
    changedSince: 'Ändrad sedan klar',
    markAsReady: 'Markera som klar',
    peopleSee: 'Andra ser v{n} tills du gör det',
    marked: 'v{n} markerad som klar',
    unmark: 'Ta bort klar-markering',
    whatChanged: 'Vad som ändrades',
    goBack: 'Gå tillbaka',
    wentBack: 'Gick tillbaka till v{n}',
    editsKept: 'Osparade ändringar behölls',
    created: 'Skapad',
    markThisReady: 'Markera den här som klar',
    tabActivity: 'Aktivitet',
    tabDocument: 'Dokument',
    tabSlides: 'Bilder',
    versionsHelp: '{kind}: ändringar i hur det ser ut och vad det gör.',
    activityHelp: 'Vad som hände medan folk använde det.',
    activityEmpty: 'Inget har hänt än.',
    dataSummary: 'Sparade: {count} · senast {time}',
    dataShowMore: 'Visa mer',
    dataShowRaw: 'Visa rådata',
    dataCopy: 'Kopiera',
    dataMoreColumns: '+{n} till',
    dataItemsNoun: 'one:post|other:poster',
    dataAutoFields: 'Fält valda automatiskt',
    sharedBeforeReady:
      'Personer som det delats med ser den aktuella versionen tills du markerar den som klar.',
  },
  da: {
    draft: 'Kladde',
    ready: 'Klar',
    changedSince: 'Ændret siden klar',
    markAsReady: 'Markér som klar',
    peopleSee: 'Andre ser v{n}, indtil du gør det',
    marked: 'v{n} markeret som klar',
    unmark: 'Fjern markering som klar',
    whatChanged: 'Hvad der er ændret',
    goBack: 'Gå tilbage',
    wentBack: 'Gik tilbage til v{n}',
    editsKept: 'Ugemte ændringer bevaret',
    created: 'Oprettet',
    markThisReady: 'Markér denne som klar',
    tabActivity: 'Aktivitet',
    tabDocument: 'Dokument',
    tabSlides: 'Dias',
    versionsHelp: '{kind}: ændringer i, hvordan det ser ud, og hvad det gør.',
    activityHelp: 'Hvad der skete, mens folk brugte det.',
    activityEmpty: 'Der er ikke sket noget endnu.',
    dataSummary: 'Gemt: {count} · senest {time}',
    dataShowMore: 'Vis mere',
    dataShowRaw: 'Vis rådata',
    dataCopy: 'Kopiér',
    dataMoreColumns: '+{n} mere',
    dataItemsNoun: 'one:element|other:elementer',
    dataAutoFields: 'Felter valgt automatisk',
    sharedBeforeReady:
      'Personer, det er delt med, ser den aktuelle version, indtil du markerer den som klar.',
  },
  de: {
    draft: 'Entwurf',
    ready: 'Bereit',
    changedSince: 'Seit „Bereit“ geändert',
    markAsReady: 'Als bereit markieren',
    peopleSee: 'Andere sehen v{n}, bis Sie das tun',
    marked: 'v{n} als bereit markiert',
    unmark: 'Markierung „Bereit“ entfernen',
    whatChanged: 'Was sich geändert hat',
    goBack: 'Zurückgehen',
    wentBack: 'Zu v{n} zurückgekehrt',
    editsKept: 'Nicht gespeicherte Änderungen behalten',
    created: 'Erstellt',
    markThisReady: 'Diese als bereit markieren',
    tabActivity: 'Aktivität',
    tabDocument: 'Dokument',
    tabSlides: 'Folien',
    versionsHelp: '{kind}: Änderungen daran, wie es aussieht und was es tut.',
    activityHelp: 'Was passiert ist, während Leute es genutzt haben.',
    activityEmpty: 'Bisher ist nichts passiert.',
    dataSummary: 'Gespeichert: {count} · zuletzt {time}',
    dataShowMore: 'Mehr anzeigen',
    dataShowRaw: 'Rohdaten anzeigen',
    dataCopy: 'Kopieren',
    dataMoreColumns: '+{n} weitere',
    dataItemsNoun: 'one:Eintrag|other:Einträge',
    dataAutoFields: 'Automatisch ausgewählte Felder',
    sharedBeforeReady:
      'Personen, mit denen es geteilt ist, sehen die aktuelle Version, bis Sie sie als bereit markieren.',
  },
  fr: {
    draft: 'Brouillon',
    ready: 'Prêt',
    changedSince: 'Modifié depuis « Prêt »',
    markAsReady: 'Marquer comme prêt',
    peopleSee: 'Les autres voient v{n} jusque-là',
    marked: 'v{n} marquée comme prête',
    unmark: 'Retirer « Prêt »',
    whatChanged: 'Ce qui a changé',
    goBack: 'Revenir en arrière',
    wentBack: 'Retour à v{n}',
    editsKept: 'Modifications non enregistrées conservées',
    created: 'Créée',
    markThisReady: 'Marquer celle-ci comme prête',
    tabActivity: 'Activité',
    tabDocument: 'Document',
    tabSlides: 'Diapositives',
    versionsHelp: '{kind} : modifications de son apparence et de son fonctionnement.',
    activityHelp: 'Ce qui s’est passé pendant son utilisation.',
    activityEmpty: 'Rien ne s’est encore passé.',
    dataSummary: 'Enregistrés : {count} · dernier {time}',
    dataShowMore: 'Afficher plus',
    dataShowRaw: 'Afficher les données brutes',
    dataCopy: 'Copier',
    dataMoreColumns: '+{n} de plus',
    dataItemsNoun: 'one:élément|other:éléments',
    dataAutoFields: 'Champs choisis automatiquement',
    sharedBeforeReady:
      'Les personnes avec qui c’est partagé voient la version actuelle jusqu’à ce que vous la marquiez comme prête.',
  },
  es: {
    draft: 'Borrador',
    ready: 'Listo',
    changedSince: 'Cambiado desde Listo',
    markAsReady: 'Marcar como listo',
    peopleSee: 'Los demás ven v{n} hasta que lo hagas',
    marked: 'v{n} marcada como lista',
    unmark: 'Quitar marca de listo',
    whatChanged: 'Qué cambió',
    goBack: 'Volver',
    wentBack: 'Se volvió a v{n}',
    editsKept: 'Se conservaron los cambios sin guardar',
    created: 'Creada',
    markThisReady: 'Marcar esta como lista',
    tabActivity: 'Actividad',
    tabDocument: 'Documento',
    tabSlides: 'Diapositivas',
    versionsHelp: '{kind}: cambios en su aspecto y en lo que hace.',
    activityHelp: 'Lo que pasó mientras la gente lo usaba.',
    activityEmpty: 'Todavía no ha pasado nada.',
    dataSummary: 'Guardados: {count} · último {time}',
    dataShowMore: 'Mostrar más',
    dataShowRaw: 'Mostrar datos sin procesar',
    dataCopy: 'Copiar',
    dataMoreColumns: '+{n} más',
    dataItemsNoun: 'one:elemento|other:elementos',
    dataAutoFields: 'Campos elegidos automáticamente',
    sharedBeforeReady:
      'Las personas con quienes se compartió ven la versión actual hasta que la marques como lista.',
  },
  it: {
    draft: 'Bozza',
    ready: 'Pronto',
    changedSince: 'Modificato da quando è pronto',
    markAsReady: 'Segna come pronto',
    peopleSee: 'Gli altri vedono v{n} finché non lo fai',
    marked: 'v{n} segnata come pronta',
    unmark: 'Togli il segno di pronto',
    whatChanged: 'Cosa è cambiato',
    goBack: 'Torna indietro',
    wentBack: 'Tornato a v{n}',
    editsKept: 'Modifiche non salvate conservate',
    created: 'Creata',
    markThisReady: 'Segna questa come pronta',
    tabActivity: 'Attività',
    tabDocument: 'Documento',
    tabSlides: 'Diapositive',
    versionsHelp: '{kind}: modifiche all’aspetto e a ciò che fa.',
    activityHelp: 'Cosa è successo mentre veniva usato.',
    activityEmpty: 'Non è ancora successo nulla.',
    dataSummary: 'Salvati: {count} · ultimo {time}',
    dataShowMore: 'Mostra altro',
    dataShowRaw: 'Mostra dati grezzi',
    dataCopy: 'Copia',
    dataMoreColumns: '+{n} altre',
    dataItemsNoun: 'one:elemento|other:elementi',
    dataAutoFields: 'Campi scelti automaticamente',
    sharedBeforeReady:
      'Le persone con cui è condiviso vedono la versione attuale finché non la segni come pronta.',
  },
  pt: {
    draft: 'Rascunho',
    ready: 'Pronto',
    changedSince: 'Alterado desde Pronto',
    markAsReady: 'Marcar como pronto',
    peopleSee: 'As outras pessoas veem v{n} até o fazer',
    marked: 'v{n} marcada como pronta',
    unmark: 'Desmarcar como pronto',
    whatChanged: 'O que mudou',
    goBack: 'Voltar atrás',
    wentBack: 'Voltou a v{n}',
    editsKept: 'Alterações não guardadas mantidas',
    created: 'Criada',
    markThisReady: 'Marcar esta como pronta',
    tabActivity: 'Atividade',
    tabDocument: 'Documento',
    tabSlides: 'Diapositivos',
    versionsHelp: '{kind}: alterações ao aspeto e ao que faz.',
    activityHelp: 'O que aconteceu enquanto foi usado.',
    activityEmpty: 'Ainda não aconteceu nada.',
    dataSummary: 'Guardados: {count} · último {time}',
    dataShowMore: 'Mostrar mais',
    dataShowRaw: 'Mostrar dados brutos',
    dataCopy: 'Copiar',
    dataMoreColumns: '+{n} mais',
    dataItemsNoun: 'one:item|many:de itens|other:itens',
    dataAutoFields: 'Campos escolhidos automaticamente',
    sharedBeforeReady:
      'As pessoas com quem foi partilhado veem a versão atual até a marcar como pronta.',
  },
  nl: {
    draft: 'Concept',
    ready: 'Klaar',
    changedSince: 'Gewijzigd sinds klaar',
    markAsReady: 'Markeren als klaar',
    peopleSee: 'Anderen zien v{n} tot je dat doet',
    marked: 'v{n} gemarkeerd als klaar',
    unmark: 'Markering klaar weghalen',
    whatChanged: 'Wat er is gewijzigd',
    goBack: 'Teruggaan',
    wentBack: 'Teruggegaan naar v{n}',
    editsKept: 'Niet-opgeslagen wijzigingen behouden',
    created: 'Gemaakt',
    markThisReady: 'Deze markeren als klaar',
    tabActivity: 'Activiteit',
    tabDocument: 'Document',
    tabSlides: 'Dia’s',
    versionsHelp: '{kind}: wijzigingen in hoe het eruitziet en wat het doet.',
    activityHelp: 'Wat er gebeurde terwijl mensen het gebruikten.',
    activityEmpty: 'Er is nog niets gebeurd.',
    dataSummary: 'Opgeslagen: {count} · laatste {time}',
    dataShowMore: 'Meer tonen',
    dataShowRaw: 'Ruwe gegevens tonen',
    dataCopy: 'Kopieer',
    dataMoreColumns: '+{n} meer',
    dataItemsNoun: 'one:item|other:items',
    dataAutoFields: 'Automatisch gekozen velden',
    sharedBeforeReady:
      'Mensen met wie het is gedeeld, zien de huidige versie tot je die markeert als klaar.',
  },
  fi: {
    draft: 'Luonnos',
    ready: 'Valmis',
    changedSince: 'Muutettu valmiiksi merkitsemisen jälkeen',
    markAsReady: 'Merkitse valmiiksi',
    peopleSee: 'Muut näkevät version v{n}, kunnes teet sen',
    marked: 'Versio v{n} merkitty valmiiksi',
    unmark: 'Poista valmis-merkintä',
    whatChanged: 'Mikä muuttui',
    goBack: 'Palaa',
    wentBack: 'Palattiin versioon v{n}',
    editsKept: 'Tallentamattomat muutokset säilytettiin',
    created: 'Luotu',
    markThisReady: 'Merkitse tämä valmiiksi',
    tabActivity: 'Toiminta',
    tabDocument: 'Asiakirja',
    tabSlides: 'Diat',
    versionsHelp: '{kind}: muutokset ulkoasuun ja toimintaan.',
    activityHelp: 'Mitä tapahtui, kun sitä käytettiin.',
    activityEmpty: 'Mitään ei ole vielä tapahtunut.',
    dataSummary: 'Tallennettu: {count} · viimeisin {time}',
    dataShowMore: 'Näytä lisää',
    dataShowRaw: 'Näytä raakadata',
    dataCopy: 'Kopioi',
    dataMoreColumns: '+{n} lisää',
    dataItemsNoun: 'one:kohde|other:kohdetta',
    dataAutoFields: 'Automaattisesti valitut kentät',
    sharedBeforeReady:
      'Ne, joille tämä on jaettu, näkevät nykyisen version, kunnes merkitset sen valmiiksi.',
  },
  et: {
    draft: 'Mustand',
    ready: 'Valmis',
    changedSince: 'Muudetud pärast valmis märkimist',
    markAsReady: 'Märgi valmis',
    peopleSee: 'Teised näevad versiooni v{n}, kuni sa seda teed',
    marked: 'Versioon v{n} märgiti valmis',
    unmark: 'Eemalda valmis märge',
    whatChanged: 'Mis muutus',
    goBack: 'Mine tagasi',
    wentBack: 'Mindi tagasi versioonile v{n}',
    editsKept: 'Salvestamata muudatused jäeti alles',
    created: 'Loodud',
    markThisReady: 'Märgi see valmis',
    tabActivity: 'Tegevus',
    tabDocument: 'Dokument',
    tabSlides: 'Slaidid',
    versionsHelp: '{kind}: muudatused välimuses ja selles, mida see teeb.',
    activityHelp: 'Mis juhtus, kui inimesed seda kasutasid.',
    activityEmpty: 'Midagi pole veel juhtunud.',
    dataSummary: 'Salvestatud: {count} · viimane {time}',
    dataShowMore: 'Näita rohkem',
    dataShowRaw: 'Näita tooreid andmeid',
    dataCopy: 'Kopeeri',
    dataMoreColumns: '+{n} veel',
    dataItemsNoun: 'one:üksus|other:üksust',
    dataAutoFields: 'Automaatselt valitud väljad',
    sharedBeforeReady:
      'Need, kellega see on jagatud, näevad praegust versiooni, kuni märgid selle valmis.',
  },
  bg: {
    draft: 'Чернова',
    ready: 'Готово',
    changedSince: 'Променено след „Готово“',
    markAsReady: 'Маркиране като готово',
    peopleSee: 'Другите виждат v{n}, докато не го направите',
    marked: 'v{n} е маркирана като готова',
    unmark: 'Премахване на „Готово“',
    whatChanged: 'Какво се промени',
    goBack: 'Назад',
    wentBack: 'Върнато към v{n}',
    editsKept: 'Незапазените редакции остават',
    created: 'Създадено',
    markThisReady: 'Маркиране на тази като готова',
    tabActivity: 'Дейност',
    tabDocument: 'Документ',
    tabSlides: 'Слайдове',
    versionsHelp: '{kind}: промени във вида и в това, което прави.',
    activityHelp: 'Какво се случи, докато хората го използваха.',
    activityEmpty: 'Все още нищо не се е случило.',
    dataSummary: 'Запазени: {count} · последно {time}',
    dataShowMore: 'Покажи още',
    dataShowRaw: 'Покажи суровите данни',
    dataCopy: 'Копиране',
    dataMoreColumns: '+{n} още',
    dataItemsNoun: 'one:елемент|other:елемента',
    dataAutoFields: 'Автоматично избрани полета',
    sharedBeforeReady:
      'Хората, с които е споделено, виждат текущата версия, докато не я маркирате като готова.',
  },
  cs: {
    draft: 'Koncept',
    ready: 'Připraveno',
    changedSince: 'Změněno od označení Připraveno',
    markAsReady: 'Označit jako připravené',
    peopleSee: 'Ostatní vidí v{n}, dokud to neuděláte',
    marked: 'v{n} označena jako připravená',
    unmark: 'Zrušit označení Připraveno',
    whatChanged: 'Co se změnilo',
    goBack: 'Vrátit se',
    wentBack: 'Vráceno na v{n}',
    editsKept: 'Neuložené úpravy zůstaly',
    created: 'Vytvořeno',
    markThisReady: 'Označit tuto jako připravenou',
    tabActivity: 'Aktivita',
    tabDocument: 'Dokument',
    tabSlides: 'Snímky',
    versionsHelp: '{kind}: změny vzhledu a toho, co dělá.',
    activityHelp: 'Co se dělo, když to lidé používali.',
    activityEmpty: 'Zatím se nic nestalo.',
    dataSummary: 'Uloženo: {count} · naposledy {time}',
    dataShowMore: 'Zobrazit více',
    dataShowRaw: 'Zobrazit nezpracovaná data',
    dataCopy: 'Kopírovat',
    dataMoreColumns: '+{n} další',
    dataItemsNoun: 'one:položka|few:položky|many:položky|other:položek',
    dataAutoFields: 'Automaticky vybraná pole',
    sharedBeforeReady:
      'Lidé, se kterými je to sdílené, vidí aktuální verzi, dokud ji neoznačíte jako připravenou.',
  },
  sk: {
    draft: 'Koncept',
    ready: 'Pripravené',
    changedSince: 'Zmenené od označenia Pripravené',
    markAsReady: 'Označiť ako pripravené',
    peopleSee: 'Ostatní vidia v{n}, kým to neurobíte',
    marked: 'v{n} označená ako pripravená',
    unmark: 'Zrušiť označenie Pripravené',
    whatChanged: 'Čo sa zmenilo',
    goBack: 'Vrátiť sa',
    wentBack: 'Vrátené na v{n}',
    editsKept: 'Neuložené úpravy zostali',
    created: 'Vytvorené',
    markThisReady: 'Označiť túto ako pripravenú',
    tabActivity: 'Aktivita',
    tabDocument: 'Dokument',
    tabSlides: 'Snímky',
    versionsHelp: '{kind}: zmeny vzhľadu a toho, čo robí.',
    activityHelp: 'Čo sa dialo, keď to ľudia používali.',
    activityEmpty: 'Zatiaľ sa nič nestalo.',
    dataSummary: 'Uložené: {count} · naposledy {time}',
    dataShowMore: 'Zobraziť viac',
    dataShowRaw: 'Zobraziť nespracované údaje',
    dataCopy: 'Kopírovať',
    dataMoreColumns: '+{n} ďalšie',
    dataItemsNoun: 'one:položka|few:položky|many:položky|other:položiek',
    dataAutoFields: 'Automaticky vybrané polia',
    sharedBeforeReady:
      'Ľudia, s ktorými je to zdieľané, vidia aktuálnu verziu, kým ju neoznačíte ako pripravenú.',
  },
  pl: {
    draft: 'Wersja robocza',
    ready: 'Gotowe',
    changedSince: 'Zmieniono od oznaczenia jako gotowe',
    markAsReady: 'Oznacz jako gotowe',
    peopleSee: 'Inni widzą v{n}, dopóki tego nie zrobisz',
    marked: 'Oznaczono v{n} jako gotową',
    unmark: 'Cofnij oznaczenie jako gotowe',
    whatChanged: 'Co się zmieniło',
    goBack: 'Wróć',
    wentBack: 'Wrócono do v{n}',
    editsKept: 'Niezapisane zmiany zachowane',
    created: 'Utworzono',
    markThisReady: 'Oznacz tę jako gotową',
    tabActivity: 'Aktywność',
    tabDocument: 'Dokument',
    tabSlides: 'Slajdy',
    versionsHelp: '{kind}: zmiany wyglądu i działania.',
    activityHelp: 'Co się działo, gdy ludzie z tego korzystali.',
    activityEmpty: 'Na razie nic się nie wydarzyło.',
    dataSummary: 'Zapisane: {count} · ostatnio {time}',
    dataShowMore: 'Pokaż więcej',
    dataShowRaw: 'Pokaż surowe dane',
    dataCopy: 'Kopiuj',
    dataMoreColumns: '+{n} więcej',
    dataItemsNoun: 'one:element|few:elementy|many:elementów|other:elementu',
    dataAutoFields: 'Pola wybrane automatycznie',
    sharedBeforeReady:
      'Osoby, którym to udostępniono, widzą bieżącą wersję, dopóki nie oznaczysz jej jako gotowej.',
  },
  hr: {
    draft: 'Nacrt',
    ready: 'Spremno',
    changedSince: 'Promijenjeno nakon oznake Spremno',
    markAsReady: 'Označi kao spremno',
    peopleSee: 'Drugi vide v{n} dok to ne učinite',
    marked: 'v{n} označena kao spremna',
    unmark: 'Ukloni oznaku Spremno',
    whatChanged: 'Što se promijenilo',
    goBack: 'Vrati se',
    wentBack: 'Vraćeno na v{n}',
    editsKept: 'Nespremljene izmjene zadržane',
    created: 'Stvoreno',
    markThisReady: 'Označi ovu kao spremnu',
    tabActivity: 'Aktivnost',
    tabDocument: 'Dokument',
    tabSlides: 'Slajdovi',
    versionsHelp: '{kind}: promjene izgleda i onoga što radi.',
    activityHelp: 'Što se događalo dok su ga ljudi koristili.',
    activityEmpty: 'Još se ništa nije dogodilo.',
    dataSummary: 'Spremljeno: {count} · zadnje {time}',
    dataShowMore: 'Prikaži više',
    dataShowRaw: 'Prikaži neobrađene podatke',
    dataCopy: 'Kopiraj',
    dataMoreColumns: '+{n} više',
    dataItemsNoun: 'one:stavka|few:stavke|other:stavki',
    dataAutoFields: 'Automatski odabrana polja',
    sharedBeforeReady:
      'Osobe s kojima je podijeljeno vide trenutačnu verziju dok je ne označite kao spremnu.',
  },
  sl: {
    draft: 'Osnutek',
    ready: 'Pripravljeno',
    changedSince: 'Spremenjeno po oznaki Pripravljeno',
    markAsReady: 'Označi kot pripravljeno',
    peopleSee: 'Drugi vidijo v{n}, dokler tega ne storite',
    marked: 'v{n} označena kot pripravljena',
    unmark: 'Odstrani oznako Pripravljeno',
    whatChanged: 'Kaj se je spremenilo',
    goBack: 'Pojdi nazaj',
    wentBack: 'Vrnjeno na v{n}',
    editsKept: 'Neshranjene spremembe ohranjene',
    created: 'Ustvarjeno',
    markThisReady: 'Označi to kot pripravljeno',
    tabActivity: 'Dejavnost',
    tabDocument: 'Dokument',
    tabSlides: 'Diapozitivi',
    versionsHelp: '{kind}: spremembe videza in delovanja.',
    activityHelp: 'Kaj se je dogajalo, ko so ga ljudje uporabljali.',
    activityEmpty: 'Zaenkrat se ni zgodilo nič.',
    dataSummary: 'Shranjeno: {count} · zadnje {time}',
    dataShowMore: 'Prikaži več',
    dataShowRaw: 'Prikaži neobdelane podatke',
    dataCopy: 'Kopiraj',
    dataMoreColumns: '+{n} več',
    dataItemsNoun: 'one:element|two:elementa|few:elementi|other:elementov',
    dataAutoFields: 'Samodejno izbrana polja',
    sharedBeforeReady:
      'Osebe, s katerimi je deljeno, vidijo trenutno različico, dokler je ne označite kot pripravljene.',
  },
  hu: {
    draft: 'Piszkozat',
    ready: 'Kész',
    changedSince: 'Módosult a Kész jelölés óta',
    markAsReady: 'Megjelölés készként',
    peopleSee: 'Mások a v{n} verziót látják, amíg ezt meg nem teszi',
    marked: 'v{n} megjelölve készként',
    unmark: 'Kész jelölés eltávolítása',
    whatChanged: 'Mi változott',
    goBack: 'Vissza',
    wentBack: 'Visszaállt a v{n} verzióra',
    editsKept: 'Nem mentett módosítások megmaradtak',
    created: 'Létrehozva',
    markThisReady: 'Ennek megjelölése készként',
    tabActivity: 'Tevékenység',
    tabDocument: 'Dokumentum',
    tabSlides: 'Diák',
    versionsHelp: '{kind}: a megjelenés és a működés módosításai.',
    activityHelp: 'Mi történt, amíg használták.',
    activityEmpty: 'Még nem történt semmi.',
    dataSummary: 'Mentve: {count} · legutóbb {time}',
    dataShowMore: 'Több megjelenítése',
    dataShowRaw: 'Nyers adatok megjelenítése',
    dataCopy: 'Másolás',
    dataMoreColumns: '+{n} további',
    dataItemsNoun: 'one:elem|other:elem',
    dataAutoFields: 'Automatikusan kiválasztott mezők',
    sharedBeforeReady:
      'Akikkel megosztotta, az aktuális verziót látják, amíg meg nem jelöli készként.',
  },
  el: {
    draft: 'Πρόχειρο',
    ready: 'Έτοιμο',
    changedSince: 'Άλλαξε μετά τη σήμανση «Έτοιμο»',
    markAsReady: 'Σήμανση ως έτοιμο',
    peopleSee: 'Οι άλλοι βλέπουν την v{n} μέχρι να το κάνετε',
    marked: 'Η v{n} σημάνθηκε ως έτοιμη',
    unmark: 'Αφαίρεση σήμανσης «Έτοιμο»',
    whatChanged: 'Τι άλλαξε',
    goBack: 'Επιστροφή',
    wentBack: 'Επιστροφή στην v{n}',
    editsKept: 'Οι μη αποθηκευμένες αλλαγές διατηρήθηκαν',
    created: 'Δημιουργήθηκε',
    markThisReady: 'Σήμανση αυτής ως έτοιμης',
    tabActivity: 'Δραστηριότητα',
    tabDocument: 'Έγγραφο',
    tabSlides: 'Διαφάνειες',
    versionsHelp: '{kind}: αλλαγές στην εμφάνιση και στη λειτουργία.',
    activityHelp: 'Τι συνέβη όσο το χρησιμοποιούσαν.',
    activityEmpty: 'Δεν έχει συμβεί τίποτα ακόμη.',
    dataSummary: 'Αποθηκευμένα: {count} · τελευταίο {time}',
    dataShowMore: 'Εμφάνιση περισσότερων',
    dataShowRaw: 'Εμφάνιση ακατέργαστων δεδομένων',
    dataCopy: 'Αντιγραφή',
    dataMoreColumns: '+{n} ακόμη',
    dataItemsNoun: 'one:στοιχείο|other:στοιχεία',
    dataAutoFields: 'Πεδία που επιλέχθηκαν αυτόματα',
    sharedBeforeReady:
      'Όσοι έχουν πρόσβαση βλέπουν την τρέχουσα έκδοση μέχρι να τη σημάνετε ως έτοιμη.',
  },
  ro: {
    draft: 'Ciornă',
    ready: 'Gata',
    changedSince: 'Modificat de la marcarea ca gata',
    markAsReady: 'Marchează ca gata',
    peopleSee: 'Ceilalți văd v{n} până când faceți acest lucru',
    marked: 'v{n} marcată ca gata',
    unmark: 'Anulează marcarea ca gata',
    whatChanged: 'Ce s-a schimbat',
    goBack: 'Înapoi',
    wentBack: 'S-a revenit la v{n}',
    editsKept: 'Modificările nesalvate au fost păstrate',
    created: 'Creată',
    markThisReady: 'Marchează aceasta ca gata',
    tabActivity: 'Activitate',
    tabDocument: 'Document',
    tabSlides: 'Diapozitive',
    versionsHelp: '{kind}: modificări ale aspectului și ale funcționării.',
    activityHelp: 'Ce s-a întâmplat cât a fost folosit.',
    activityEmpty: 'Nu s-a întâmplat nimic încă.',
    dataSummary: 'Salvate: {count} · cel mai recent {time}',
    dataShowMore: 'Afișează mai mult',
    dataShowRaw: 'Afișează datele brute',
    dataCopy: 'Copiere',
    dataMoreColumns: '+{n} în plus',
    dataItemsNoun: 'one:element|few:elemente|other:de elemente',
    dataAutoFields: 'Câmpuri alese automat',
    sharedBeforeReady:
      'Persoanele cu care a fost partajat văd versiunea actuală până când o marcați ca gata.',
  },
  lt: {
    draft: 'Juodraštis',
    ready: 'Paruošta',
    changedSince: 'Pakeista po pažymėjimo Paruošta',
    markAsReady: 'Pažymėti kaip paruoštą',
    peopleSee: 'Kiti mato v{n}, kol to nepadarysite',
    marked: 'v{n} pažymėta kaip paruošta',
    unmark: 'Nuimti žymą Paruošta',
    whatChanged: 'Kas pasikeitė',
    goBack: 'Grįžti',
    wentBack: 'Grįžta į v{n}',
    editsKept: 'Neišsaugoti pakeitimai išlaikyti',
    created: 'Sukurta',
    markThisReady: 'Pažymėti šią kaip paruoštą',
    tabActivity: 'Veikla',
    tabDocument: 'Dokumentas',
    tabSlides: 'Skaidrės',
    versionsHelp: '{kind}: išvaizdos ir veikimo pakeitimai.',
    activityHelp: 'Kas vyko, kol žmonės tuo naudojosi.',
    activityEmpty: 'Kol kas nieko neįvyko.',
    dataSummary: 'Išsaugota: {count} · naujausias {time}',
    dataShowMore: 'Rodyti daugiau',
    dataShowRaw: 'Rodyti neapdorotus duomenis',
    dataCopy: 'Kopijuoti',
    dataMoreColumns: '+{n} daugiau',
    dataItemsNoun: 'one:elementas|few:elementai|many:elemento|other:elementų',
    dataAutoFields: 'Automatiškai parinkti laukai',
    sharedBeforeReady:
      'Žmonės, su kuriais bendrinama, mato dabartinę versiją, kol nepažymėsite jos kaip paruoštos.',
  },
  lv: {
    draft: 'Melnraksts',
    ready: 'Gatavs',
    changedSince: 'Mainīts kopš atzīmes Gatavs',
    markAsReady: 'Atzīmēt kā gatavu',
    peopleSee: 'Citi redz v{n}, līdz jūs to izdarāt',
    marked: 'v{n} atzīmēta kā gatava',
    unmark: 'Noņemt atzīmi Gatavs',
    whatChanged: 'Kas mainījās',
    goBack: 'Atgriezties',
    wentBack: 'Atgriezts uz v{n}',
    editsKept: 'Nesaglabātie labojumi paturēti',
    created: 'Izveidots',
    markThisReady: 'Atzīmēt šo kā gatavu',
    tabActivity: 'Darbība',
    tabDocument: 'Dokuments',
    tabSlides: 'Slaidi',
    versionsHelp: '{kind}: izskata un darbības izmaiņas.',
    activityHelp: 'Kas notika, kamēr cilvēki to izmantoja.',
    activityEmpty: 'Pagaidām nekas nav noticis.',
    dataSummary: 'Saglabāti: {count} · jaunākais {time}',
    dataShowMore: 'Rādīt vairāk',
    dataShowRaw: 'Rādīt neapstrādātos datus',
    dataCopy: 'Kopēt',
    dataMoreColumns: '+{n} vēl',
    dataItemsNoun: 'zero:elementu|one:elements|other:elementi',
    dataAutoFields: 'Automātiski izvēlēti lauki',
    sharedBeforeReady:
      'Cilvēki, ar kuriem tas kopīgots, redz pašreizējo versiju, līdz jūs to atzīmēsiet kā gatavu.',
  },
  ga: {
    draft: 'Dréacht',
    ready: 'Réidh',
    changedSince: 'Athraithe ó marcáladh réidh é',
    markAsReady: 'Marcáil mar réidh',
    peopleSee: 'Feiceann daoine eile v{n} go dtí go ndéanann tú é sin',
    marked: 'Marcáladh v{n} mar réidh',
    unmark: 'Bain an marc réidh',
    whatChanged: 'Cad a d’athraigh',
    goBack: 'Téigh siar',
    wentBack: 'Chuathas siar go v{n}',
    editsKept: 'Coinníodh athruithe nár sábháladh',
    created: 'Cruthaithe',
    markThisReady: 'Marcáil é seo mar réidh',
    tabActivity: 'Gníomhaíocht',
    tabDocument: 'Doiciméad',
    tabSlides: 'Sleamhnáin',
    versionsHelp: '{kind}: athruithe ar an gcuma agus ar a ndéanann sé.',
    activityHelp: 'An méid a tharla agus daoine á úsáid.',
    activityEmpty: 'Níor tharla aon rud fós.',
    dataSummary: 'Sábháilte: {count} · is déanaí {time}',
    dataShowMore: 'Taispeáin tuilleadh',
    dataShowRaw: 'Taispeáin na sonraí amha',
    dataCopy: 'Cóipeáil',
    dataMoreColumns: '+{n} eile',
    dataItemsNoun: 'one:mhír|two:mhír|few:mhír|many:mír|other:mír',
    dataAutoFields: 'Réimsí a roghnaíodh go huathoibríoch',
    sharedBeforeReady:
      'Feiceann na daoine lena roinntear é an leagan reatha go dtí go marcálann tú réidh é.',
  },
  mt: {
    draft: 'Abbozz',
    ready: 'Lest',
    changedSince: 'Inbidel minn meta mmarkat bħala lest',
    markAsReady: 'Immarka bħala lest',
    peopleSee: 'Ħaddieħor jara v{n} sakemm tagħmel dan',
    marked: 'v{n} immarkata bħala lesta',
    unmark: 'Neħħi l-marka ta’ lest',
    whatChanged: 'X’inbidel',
    goBack: 'Mur lura',
    wentBack: 'Mort lura għal v{n}',
    editsKept: 'Il-bidliet mhux salvati nżammu',
    created: 'Maħluq',
    markThisReady: 'Immarka din bħala lesta',
    tabActivity: 'Attività',
    tabDocument: 'Dokument',
    tabSlides: 'Slajds',
    versionsHelp: '{kind}: bidliet fid-dehra u f’dak li jagħmel.',
    activityHelp: 'X’ġara waqt li n-nies kienu qed jużawh.',
    activityEmpty: 'Għadu ma ġara xejn.',
    dataSummary: 'Salvati: {count} · l-aħħar {time}',
    dataShowMore: 'Uri aktar',
    dataShowRaw: 'Uri d-dejta mhux ipproċessata',
    dataCopy: 'Kopja',
    dataMoreColumns: '+{n} oħra',
    dataItemsNoun: 'one:element|two:elementi|few:elementi|many:element|other:element',
    dataAutoFields: 'Oqsma magħżula awtomatikament',
    sharedBeforeReady:
      'In-nies li magħhom huwa maqsum jaraw il-verżjoni attwali sakemm timmarkaha bħala lesta.',
  },
};
export default readyCopy;
